/**
 * Collation Store for Enhanced Segmentation Agent v2.0
 * 
 * Manages collations - saved alignments that serve as anchors for further witness alignment.
 * Supports versioning, editing, and export functionality.
 * 
 * Author: Enhanced Segmentation Agent v2.0
 * Date: June 27, 2025
 */

import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { API_BASE_URL } from '../config/api'
import { instrumentIndexedDBWriteTransactions } from '../lib/indexedDBSnapshotGuard'
import {
  assertLedgerSafePostgresPrimaryOperationSupported,
  runLedgerSafePostgresPrimaryWrite,
} from '../lib/ledgerSafePostgresPrimaryWriteBridge'

// ============================================================================
// Core Data Types
// ============================================================================

export interface Collation {
  id: string
  name: string
  description?: string
  created_at: number
  updated_at: number
  version: number
  
  // Source data
  book_id: string
  chapter_id: string
  source_alignment_id?: string // Original alignment that created this
  
  // The actual collation data (essentially a saved alignment)
  aligned_segments: AlignedSegment[]
  witness_metadata: WitnessMetadata[]
  
  // Status
  status: 'active' | 'archived'
}

export interface AlignedSegment {
  position: number // Row number in collation
  segment_data: {
    [witness_id: string]: {
      text: string
      confidence_score: number
      unit_id?: string
      chunk_id?: string
    }
  }
}

export interface WitnessMetadata {
  witness_id: string
  witness_name: string
  sigla: string
  added_at: number
  processing_metadata?: any
}

export interface CollationVersion {
  collation_id: string
  version: number
  changes: CollationChange[]
  created_at: number
  created_by: string
  description?: string
}

export interface CollationChange {
  type: 'insert' | 'update' | 'delete' | 'add_witness' | 'remove_witness'
  position?: number
  witness_id?: string
  old_value?: any
  new_value?: any
  timestamp: number
}

export interface AlignmentPreview {
  collation_id: string
  new_witness_id: string
  proposed_changes: CollationChange[]
  confidence_scores: number[]
  novel_segments: AlignedSegment[]
  modified_segments: AlignedSegment[]
}

// ============================================================================
// Store Interface
// ============================================================================

interface CollationStore {
  // State
  collations: Map<string, Collation>
  currentCollation: Collation | null
  versions: Map<string, CollationVersion[]>
  isLoading: boolean
  error: string | null
  
  // Basic CRUD operations
  loadCollations: () => Promise<void>
  createCollation: (collationData: Omit<Collation, 'id' | 'created_at' | 'updated_at' | 'version'>) => Promise<string>
  loadCollation: (collationId: string) => Promise<void>
  updateCollation: (collationId: string, updates: Partial<Collation>) => Promise<void>
  deleteCollation: (collationId: string) => Promise<void>
  
  // Versioning
  createVersion: (collationId: string, changes: CollationChange[], description?: string) => Promise<void>
  loadVersions: (collationId: string) => Promise<void>
  rollbackToVersion: (collationId: string, version: number) => Promise<void>
  
  // Editing operations
  updateSegment: (collationId: string, position: number, witnessId: string, newText: string) => Promise<void>
  insertSegment: (collationId: string, position: number, segmentData: AlignedSegment['segment_data']) => Promise<void>
  deleteSegment: (collationId: string, position: number) => Promise<void>
  addWitness: (collationId: string, witnessMetadata: WitnessMetadata) => Promise<void>
  removeWitness: (collationId: string, witnessId: string) => Promise<void>
  
  // Alignment operations
  saveAlignmentAsCollation: (alignmentResult: any, name: string, description?: string) => Promise<string>
  alignWitnessToCollation: (witnessResult: any, collationId: string) => Promise<AlignmentPreview>
  applyAlignment: (preview: AlignmentPreview) => Promise<void>
  
  // Export operations
  exportCollation: (collationId: string, format: 'json' | 'pdf') => Promise<void>
  
  // Utility functions
  getCollationsByBook: (bookId: string) => Collation[]
  getCollationsByChapter: (chapterId: string) => Collation[]
  searchCollations: (query: string) => Collation[]
  clearError: () => void
  setCurrentCollation: (collation: Collation | null) => void
}

// ============================================================================
// Storage Layer
// ============================================================================

export const LEGACY_COLLATION_STORE_NAMES = ['collations', 'collation_versions'] as const
export type LegacyCollationStoreName = (typeof LEGACY_COLLATION_STORE_NAMES)[number]
export type LegacyCollationSnapshot = Record<LegacyCollationStoreName, any[]>
export const LEGACY_COLLATION_DB_NAME = 'CollationDB'
export const LEGACY_COLLATION_DB_VERSION = 1
export const LEGACY_COLLATION_STORE_KEY_PATHS: Record<LegacyCollationStoreName, string | readonly string[]> = {
  collations: 'id',
  collation_versions: ['collation_id', 'version'],
}

export class CollationStorage {
  private dbName = LEGACY_COLLATION_DB_NAME
  private dbVersion = LEGACY_COLLATION_DB_VERSION
  private db: IDBDatabase | null = null

  async init(): Promise<void> {
    if (this.db) {
      return
    }
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion)
      
      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        instrumentIndexedDBWriteTransactions(this.db, this.dbName)
        resolve()
      }
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        
        // Collations store
        if (!db.objectStoreNames.contains('collations')) {
          const collationStore = db.createObjectStore('collations', { keyPath: 'id' })
          collationStore.createIndex('book_id', 'book_id', { unique: false })
          collationStore.createIndex('chapter_id', 'chapter_id', { unique: false })
          collationStore.createIndex('created_at', 'created_at', { unique: false })
          collationStore.createIndex('status', 'status', { unique: false })
        }
        
        // Versions store
        if (!db.objectStoreNames.contains('collation_versions')) {
          const versionStore = db.createObjectStore('collation_versions', { keyPath: ['collation_id', 'version'] })
          versionStore.createIndex('collation_id', 'collation_id', { unique: false })
          versionStore.createIndex('created_at', 'created_at', { unique: false })
        }
      }
    })
  }

  async saveCollation(collation: Collation): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    return runLedgerSafePostgresPrimaryWrite({
      mutations: [{
        action: 'put',
        databaseName: LEGACY_COLLATION_DB_NAME,
        storeName: 'collations',
        key: collation.id,
        value: collation,
      }],
      applyCache: () => new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction(['collations'], 'readwrite')
        const store = transaction.objectStore('collations')
        store.put(collation)
        transaction.oncomplete = () => resolve()
        transaction.onerror = () => reject(
          transaction.error || new Error('Collation write failed'),
        )
        transaction.onabort = () => reject(
          transaction.error || new Error('Collation write aborted'),
        )
      }),
    })
  }

  async getCollation(id: string): Promise<Collation | null> {
    if (!this.db) throw new Error('Database not initialized')
    
    const transaction = this.db.transaction(['collations'], 'readonly')
    const store = transaction.objectStore('collations')
    
    return new Promise((resolve, reject) => {
      const request = store.get(id)
      request.onsuccess = () => resolve(request.result || null)
      request.onerror = () => reject(request.error)
    })
  }

  async getAllCollations(): Promise<Collation[]> {
    if (!this.db) throw new Error('Database not initialized')
    
    const transaction = this.db.transaction(['collations'], 'readonly')
    const store = transaction.objectStore('collations')
    
    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async deleteCollation(id: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    return runLedgerSafePostgresPrimaryWrite({
      mutations: [{
        action: 'delete',
        databaseName: LEGACY_COLLATION_DB_NAME,
        storeName: 'collations',
        key: id,
      }],
      applyCache: () => new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction(['collations'], 'readwrite')
        const store = transaction.objectStore('collations')
        store.delete(id)
        transaction.oncomplete = () => resolve()
        transaction.onerror = () => reject(
          transaction.error || new Error('Collation delete failed'),
        )
        transaction.onabort = () => reject(
          transaction.error || new Error('Collation delete aborted'),
        )
      }),
    })
  }

  async saveVersion(version: CollationVersion): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    return runLedgerSafePostgresPrimaryWrite({
      mutations: [{
        action: 'put',
        databaseName: LEGACY_COLLATION_DB_NAME,
        storeName: 'collation_versions',
        key: [version.collation_id, version.version],
        value: version,
      }],
      applyCache: () => new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction(
          ['collation_versions'],
          'readwrite',
        )
        const store = transaction.objectStore('collation_versions')
        store.put(version)
        transaction.oncomplete = () => resolve()
        transaction.onerror = () => reject(
          transaction.error || new Error('Collation version write failed'),
        )
        transaction.onabort = () => reject(
          transaction.error || new Error('Collation version write aborted'),
        )
      }),
    })
  }

  async getVersions(collationId: string): Promise<CollationVersion[]> {
    if (!this.db) throw new Error('Database not initialized')
    
    const transaction = this.db.transaction(['collation_versions'], 'readonly')
    const store = transaction.objectStore('collation_versions')
    const index = store.index('collation_id')
    
    return new Promise((resolve, reject) => {
      const request = index.getAll(collationId)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async exportAllData(): Promise<LegacyCollationSnapshot> {
    await this.init()
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction(LEGACY_COLLATION_STORE_NAMES, 'readonly')
    const entries = await Promise.all(LEGACY_COLLATION_STORE_NAMES.map(store => (
      new Promise<[LegacyCollationStoreName, any[]]>((resolve, reject) => {
        const request = transaction.objectStore(store).getAll()
        request.onsuccess = () => resolve([store, request.result])
        request.onerror = () => reject(request.error)
      })
    )))
    return Object.fromEntries(entries) as LegacyCollationSnapshot
  }

  async replaceAllData(snapshot: LegacyCollationSnapshot): Promise<void> {
    assertLedgerSafePostgresPrimaryOperationSupported(
      'Replacing the complete CollationDB cache',
    )
    await this.init()
    if (!this.db) throw new Error('Database not initialized')
    const transaction = this.db.transaction(LEGACY_COLLATION_STORE_NAMES, 'readwrite')

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error || new Error('Legacy CollationDB replacement failed'))
      transaction.onabort = () => reject(transaction.error || new Error('Legacy CollationDB replacement aborted'))

      try {
        for (const store of LEGACY_COLLATION_STORE_NAMES) {
          const objectStore = transaction.objectStore(store)
          objectStore.clear()
          for (const record of snapshot[store] || []) {
            objectStore.put(record)
          }
        }
      } catch (error) {
        transaction.abort()
        reject(error)
      }
    })
  }

  async destroy(): Promise<void> {
    assertLedgerSafePostgresPrimaryOperationSupported(
      'Destroying CollationDB',
    )
    this.db?.close()
    this.db = null
    await new Promise<void>((resolve, reject) => {
      const request = indexedDB.deleteDatabase(this.dbName)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
      request.onblocked = () => resolve()
    })
  }
}

// Initialize storage instance
export const collationStorage = new CollationStorage()

// ============================================================================
// Store Implementation
// ============================================================================

export const useCollationStore = create<CollationStore>()(
  subscribeWithSelector((set, get) => ({
    // Initial State
    collations: new Map(),
    currentCollation: null,
    versions: new Map(),
    isLoading: false,
    error: null,

    // Basic CRUD Operations
    loadCollations: async () => {
      set({ isLoading: true, error: null })
      try {
        await collationStorage.init()
        const collations = await collationStorage.getAllCollations()
        const collationMap = new Map(collations.map(c => [c.id, c]))
        set({ collations: collationMap, isLoading: false })
      } catch (error) {
        console.error('Failed to load collations:', error)
        set({ error: `Failed to load collations: ${error}`, isLoading: false })
      }
    },

    createCollation: async (collationData) => {
      set({ isLoading: true, error: null })
      try {
        await collationStorage.init()
        
        const id = `collation_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        const now = Date.now()
        
        const collation: Collation = {
          ...collationData,
          id,
          created_at: now,
          updated_at: now,
          version: 1
        }
        
        await collationStorage.saveCollation(collation)
        
        set(state => ({
          collations: new Map(state.collations.set(id, collation)),
          isLoading: false
        }))
        
        return id
      } catch (error) {
        console.error('Failed to create collation:', error)
        set({ error: `Failed to create collation: ${error}`, isLoading: false })
        throw error
      }
    },

    loadCollation: async (collationId) => {
      set({ isLoading: true, error: null })
      try {
        await collationStorage.init()
        const collation = await collationStorage.getCollation(collationId)
        if (!collation) {
          throw new Error(`Collation ${collationId} not found`)
        }
        set({ currentCollation: collation, isLoading: false })
      } catch (error) {
        console.error('Failed to load collation:', error)
        set({ error: `Failed to load collation: ${error}`, isLoading: false })
      }
    },

    updateCollation: async (collationId, updates) => {
      set({ isLoading: true, error: null })
      try {
        await collationStorage.init()
        const current = get().collations.get(collationId)
        if (!current) {
          throw new Error(`Collation ${collationId} not found`)
        }
        
        const updated = {
          ...current,
          ...updates,
          updated_at: Date.now(),
          version: current.version + 1
        }
        
        await collationStorage.saveCollation(updated)
        
        set(state => ({
          collations: new Map(state.collations.set(collationId, updated)),
          currentCollation: state.currentCollation?.id === collationId ? updated : state.currentCollation,
          isLoading: false
        }))
      } catch (error) {
        console.error('Failed to update collation:', error)
        set({ error: `Failed to update collation: ${error}`, isLoading: false })
      }
    },

    deleteCollation: async (collationId) => {
      set({ isLoading: true, error: null })
      try {
        await collationStorage.init()
        await collationStorage.deleteCollation(collationId)
        
        set(state => {
          const newCollations = new Map(state.collations)
          newCollations.delete(collationId)
          
          return {
            collations: newCollations,
            currentCollation: state.currentCollation?.id === collationId ? null : state.currentCollation,
            isLoading: false
          }
        })
      } catch (error) {
        console.error('Failed to delete collation:', error)
        set({ error: `Failed to delete collation: ${error}`, isLoading: false })
      }
    },

    // Versioning Operations
    createVersion: async (collationId, changes, description) => {
      try {
        await collationStorage.init()
        const collation = get().collations.get(collationId)
        if (!collation) {
          throw new Error(`Collation ${collationId} not found`)
        }
        
        const version: CollationVersion = {
          collation_id: collationId,
          version: collation.version + 1,
          changes,
          created_at: Date.now(),
          created_by: 'frontend_user',
          description
        }
        
        await collationStorage.saveVersion(version)
        
        set(state => ({
          versions: new Map(state.versions.set(collationId, [...(state.versions.get(collationId) || []), version]))
        }))
      } catch (error) {
        console.error('Failed to create version:', error)
        set({ error: `Failed to create version: ${error}` })
      }
    },

    loadVersions: async (collationId) => {
      try {
        await collationStorage.init()
        const versions = await collationStorage.getVersions(collationId)
        set(state => ({
          versions: new Map(state.versions.set(collationId, versions))
        }))
      } catch (error) {
        console.error('Failed to load versions:', error)
        set({ error: `Failed to load versions: ${error}` })
      }
    },

    rollbackToVersion: async (collationId, version) => {
      // TODO: Implement rollback logic
      console.log('Rollback not yet implemented')
    },

    // Editing Operations
    updateSegment: async (collationId, position, witnessId, newText) => {
      const collation = get().collations.get(collationId)
      if (!collation) return
      
      const segment = collation.aligned_segments.find(s => s.position === position)
      if (!segment) return
      
      const oldText = segment.segment_data[witnessId]?.text || ''
      
      // Create change record
      const change: CollationChange = {
        type: 'update',
        position,
        witness_id: witnessId,
        old_value: oldText,
        new_value: newText,
        timestamp: Date.now()
      }
      
      // Update segment
      const updatedSegments = collation.aligned_segments.map(s => {
        if (s.position === position) {
          return {
            ...s,
            segment_data: {
              ...s.segment_data,
              [witnessId]: {
                ...s.segment_data[witnessId],
                text: newText
              }
            }
          }
        }
        return s
      })
      
      await get().updateCollation(collationId, { aligned_segments: updatedSegments })
      await get().createVersion(collationId, [change], `Updated segment ${position}`)
    },

    insertSegment: async (collationId, position, segmentData) => {
      const collation = get().collations.get(collationId)
      if (!collation) return
      
      const change: CollationChange = {
        type: 'insert',
        position,
        new_value: segmentData,
        timestamp: Date.now()
      }
      
      // Insert segment and adjust positions
      const updatedSegments = [...collation.aligned_segments]
      updatedSegments.splice(position, 0, { position, segment_data: segmentData })
      
      // Adjust positions of subsequent segments
      for (let i = position + 1; i < updatedSegments.length; i++) {
        updatedSegments[i] = { ...updatedSegments[i], position: i }
      }
      
      await get().updateCollation(collationId, { aligned_segments: updatedSegments })
      await get().createVersion(collationId, [change], `Inserted segment at position ${position}`)
    },

    deleteSegment: async (collationId, position) => {
      const collation = get().collations.get(collationId)
      if (!collation) return
      
      const segment = collation.aligned_segments.find(s => s.position === position)
      if (!segment) return
      
      const change: CollationChange = {
        type: 'delete',
        position,
        old_value: segment.segment_data,
        timestamp: Date.now()
      }
      
      // Remove segment and adjust positions
      const updatedSegments = collation.aligned_segments
        .filter(s => s.position !== position)
        .map((s, index) => ({ ...s, position: index }))
      
      await get().updateCollation(collationId, { aligned_segments: updatedSegments })
      await get().createVersion(collationId, [change], `Deleted segment ${position}`)
    },

    addWitness: async (collationId, witnessMetadata) => {
      const collation = get().collations.get(collationId)
      if (!collation) return
      
      const change: CollationChange = {
        type: 'add_witness',
        witness_id: witnessMetadata.witness_id,
        new_value: witnessMetadata,
        timestamp: Date.now()
      }
      
      const updatedWitnesses = [...collation.witness_metadata, witnessMetadata]
      
      await get().updateCollation(collationId, { witness_metadata: updatedWitnesses })
      await get().createVersion(collationId, [change], `Added witness ${witnessMetadata.witness_name}`)
    },

    removeWitness: async (collationId, witnessId) => {
      const collation = get().collations.get(collationId)
      if (!collation) return
      
      const witness = collation.witness_metadata.find(w => w.witness_id === witnessId)
      if (!witness) return
      
      const change: CollationChange = {
        type: 'remove_witness',
        witness_id: witnessId,
        old_value: witness,
        timestamp: Date.now()
      }
      
      // Remove witness from metadata and all segments
      const updatedWitnesses = collation.witness_metadata.filter(w => w.witness_id !== witnessId)
      const updatedSegments = collation.aligned_segments.map(segment => ({
        ...segment,
        segment_data: Object.fromEntries(
          Object.entries(segment.segment_data).filter(([id]) => id !== witnessId)
        )
      }))
      
      await get().updateCollation(collationId, { 
        witness_metadata: updatedWitnesses,
        aligned_segments: updatedSegments
      })
      await get().createVersion(collationId, [change], `Removed witness ${witness.witness_name}`)
    },

    // Alignment Operations
    saveAlignmentAsCollation: async (alignmentResult, name, description) => {
      set({ isLoading: true, error: null })
      try {
        console.log('🔍 saveAlignmentAsCollation called with:', {
          alignmentResult,
          name,
          description,
          hasWitnesses: !!alignmentResult?.witnesses,
          witnessCount: alignmentResult?.witnesses?.length,
          hasAlignedResults: !!alignmentResult?.alignedResults,
          hasAligned_results: !!alignmentResult?.aligned_results,
          firstRowStructure: alignmentResult?.alignedResults?.[0] || alignmentResult?.aligned_results?.[0]
        })

        // Validate input
        if (!alignmentResult) {
          throw new Error('No alignment result provided')
        }

        if (!alignmentResult.witnesses || !Array.isArray(alignmentResult.witnesses)) {
          throw new Error('Alignment result must contain witnesses array')
        }

        // Extract witness metadata from alignment result
        const witness_metadata: WitnessMetadata[] = alignmentResult.witnesses.map((witness: any) => ({
          witness_id: witness.id,
          witness_name: witness.name || witness.sigla,
          sigla: witness.sigla,
          added_at: Date.now(),
          processing_metadata: {
            preprocessing_result: witness.preprocessing_result,
            segmentation_result: witness.segmentation_result
          }
        }))
        
        // Convert aligned results to collation segments
        // Handle both alignedResults (from frontend) and aligned_results (from API)
        const rawResults = alignmentResult.alignedResults || alignmentResult.aligned_results || []
        console.log('🔍 Converting alignment results to collation segments:', {
          hasAlignedResults: !!alignmentResult.alignedResults,
          hasAligned_results: !!alignmentResult.aligned_results,
          resultCount: rawResults.length,
          witnessCount: alignmentResult.witnesses?.length,
          firstRow: rawResults[0]
        })

        if (!Array.isArray(rawResults)) {
          throw new Error('Alignment results must be an array')
        }

        const aligned_segments: AlignedSegment[] = []
        
        rawResults.forEach((row: any, index: number) => {
          console.log(`🔍 Processing row ${index + 1}:`, row)
          
          const segment_data: AlignedSegment['segment_data'] = {}
          
          // Check if row has witness_units (new format) or witness_a/b/c (old format)
          if (row.witness_units && typeof row.witness_units === 'object') {
            // New format: witness_units with actual witness IDs
            console.log(`🔍 Row ${index + 1} using witness_units format`)
            
            // Iterate through each witness in the alignment
            alignmentResult.witnesses.forEach((witness: any) => {
              const witnessId = witness.id
              const unitData = row.witness_units[witnessId]
              
              console.log(`🔍 Processing witness ${witnessId}:`, {
                unitData,
                witness
              })
              
              if (unitData && unitData.unit_text_content) {
                // Extract data from witness_units format
                segment_data[witnessId] = {
                  text: unitData.unit_text_content.trim(),
                  confidence_score: unitData.confidence_score || parseFloat(row.confidence_score) || 0.8,
                  unit_id: unitData.unit_id || '',
                  chunk_id: unitData.source_chunk_id || ''
                }
                
                console.log(`🔍 Extracted text for witness ${witnessId}:`, {
                  text: unitData.unit_text_content.substring(0, 50) + '...',
                  unit_id: unitData.unit_id,
                  chunk_id: unitData.source_chunk_id
                })
              } else if (unitData === null || unitData === undefined) {
                console.log(`🔍 Witness ${witnessId} has GAP in row ${index + 1}`)
              }
            })
          } else {
            // Fallback to old format (witness_a, witness_b, witness_c)
            console.log(`🔍 Row ${index + 1} using legacy witness_a/b/c format`)
            
            const witnessKeys = Object.keys(row).filter(key => key.startsWith('witness_'))
            console.log(`🔍 Row ${index + 1} witness keys:`, witnessKeys)
            
            witnessKeys.forEach((witnessKey, witnessIndex) => {
              const witnessData = row[witnessKey]
              const witness = alignmentResult.witnesses[witnessIndex]
              
              if (witness && witnessData) {
                // Handle different witness data formats
                let text = ''
                let unit_id = ''
                let chunk_id = ''
                
                if (typeof witnessData === 'string') {
                  // Direct text format
                  text = witnessData
                } else if (witnessData && typeof witnessData === 'object') {
                  // Object format
                  text = witnessData.unit_text || witnessData.text || witnessData.unit_text_content || ''
                  unit_id = witnessData.unit_id || ''
                  chunk_id = witnessData.chunk_id || witnessData.source_chunk_id || ''
                }
                
                if (text && text.trim()) { // Only add non-empty segments
                  segment_data[witness.id] = {
                    text: text.trim(),
                    confidence_score: parseFloat(row.confidence_score) || 0.8,
                    unit_id,
                    chunk_id
                  }
                }
              }
            })
          }
          
          // Only add segment if it has data for at least one witness
          if (Object.keys(segment_data).length > 0) {
            aligned_segments.push({
              position: aligned_segments.length + 1,  // Always use sequential numbering
              segment_data
            })
          }
        })
        
        console.log(`🔍 Created ${aligned_segments.length} aligned segments`)
        
        // Extract book and chapter IDs from witnesses or alignment context
        const firstWitness = alignmentResult.witnesses[0]
        const book_id = firstWitness?.book_id || alignmentResult.book_id || ''
        const chapter_id = firstWitness?.chapter_id || alignmentResult.chapter_id || ''
        
        const collationData = {
          name,
          description,
          book_id,
          chapter_id,
          source_alignment_id: alignmentResult.id,
          aligned_segments,
          witness_metadata,
          status: 'active' as const
        }
        
        console.log('🔍 Final collation data:', collationData)
        
        const collationId = await get().createCollation(collationData)
        set({ isLoading: false })
        return collationId
      } catch (error) {
        console.error('❌ Failed to save alignment as collation:', error)
        set({ error: `Failed to save alignment as collation: ${error}`, isLoading: false })
        throw error
      }
    },

    alignWitnessToCollation: async (witnessResult, collationId) => {
      // TODO: Implement alignment algorithm
      // This will use the existing alignment logic but align against collation
      throw new Error('alignWitnessToCollation not yet implemented')
    },

    applyAlignment: async (preview) => {
      // TODO: Apply alignment preview changes to collation
      throw new Error('applyAlignment not yet implemented')
    },

    // Export Operations
    exportCollation: async (collationId, format) => {
      const collation = get().collations.get(collationId)
      if (!collation) {
        throw new Error(`Collation ${collationId} not found`)
      }
      
      if (format === 'json') {
        const dataStr = JSON.stringify(collation, null, 2)
        const dataBlob = new Blob([dataStr], { type: 'application/json' })
        const url = URL.createObjectURL(dataBlob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${collation.name}.json`
        link.click()
        URL.revokeObjectURL(url)
      } else if (format === 'pdf') {
        try {
          // Call server-side PDF export endpoint
          const response = await fetch(`${API_BASE_URL}/api/collations/export/pdf`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(collation)
          })
          
          if (!response.ok) {
            throw new Error(`PDF export failed: ${response.statusText}`)
          }
          
          // Get the PDF blob
          const blob = await response.blob()
          
          // Create download link
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `${collation.name.replace(/[^a-z0-9]/gi, '_')}_collation.pdf`
          link.click()
          URL.revokeObjectURL(url)
          
          console.log('PDF exported successfully via server')
        } catch (error) {
          console.error('Failed to export PDF:', error)
          set({ error: `Failed to export PDF: ${error}` })
        }
      }
    },

    // Utility Functions
    getCollationsByBook: (bookId) => {
      return Array.from(get().collations.values()).filter(c => c.book_id === bookId)
    },

    getCollationsByChapter: (chapterId) => {
      return Array.from(get().collations.values()).filter(c => c.chapter_id === chapterId)
    },

    searchCollations: (query) => {
      const lowercaseQuery = query.toLowerCase()
      return Array.from(get().collations.values()).filter(c => 
        c.name.toLowerCase().includes(lowercaseQuery) ||
        c.description?.toLowerCase().includes(lowercaseQuery)
      )
    },

    clearError: () => set({ error: null }),

    setCurrentCollation: (collation) => set({ currentCollation: collation })
  }))
)

// Export additional types for external use
export type CollationStoreType = typeof useCollationStore
